import { useCallback, useRef, useState } from 'react'
import {
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  Images,
  Wand2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useCreateModel } from './context'
import { MIN_IMAGES } from './constants'
import { UploadZone } from './UploadZone'
import { PreprocessToolbar } from './PreprocessToolbar'
import { ValidationWarnings } from './ValidationWarnings'
import { ImageGrid } from './ImageGrid'

// ─── Auto-Caption via Florence-2 API ────────────────────────────

import { trainingAPI } from '@/lib/api/training'
import { isApiError } from '@/lib/api/api-helpers'
import { toast } from '@/components/ui/toast'

function useAutoCaption() {
  const { state, dispatch } = useCreateModel()
  const abortRef = useRef<AbortController | null>(null)

  const startCaptioning = useCallback(async () => {
    const uncaptioned = state.images.filter((img) => img.captionStatus === 'none')
    if (uncaptioned.length === 0) return

    dispatch({ type: 'START_CAPTIONING' })
    abortRef.current = new AbortController()

    for (const img of uncaptioned) {
      if (abortRef.current.signal.aborted) break

      try {
        let caption = await trainingAPI.captionImage(img.file)
        if (state.triggerWord) caption = `${state.triggerWord} ${caption}`
        dispatch({ type: 'CAPTION_IMAGE', payload: { imageId: img.id, caption } })
      } catch (err) {
        // Captioning is charged now. With an empty wallet every remaining image
        // would fail the same way, so stop and say why instead of skipping silently.
        if (isApiError(err, 'INSUFFICIENT_BALANCE')) {
          toast.error(err.message)
          break
        }
        // Any other failure: skip this image and carry on with the rest
      }
    }

    if (!abortRef.current.signal.aborted) {
      dispatch({ type: 'FINISH_CAPTIONING' })
    }
  }, [state.images, state.triggerWord, dispatch])

  const cancelCaptioning = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      dispatch({ type: 'FINISH_CAPTIONING' })
    }
  }, [dispatch])

  return { startCaptioning, cancelCaptioning }
}

// ─── Collapsible Section ────────────────────────────────────────

interface SectionProps {
  title: string
  icon: React.ReactNode
  badge?: React.ReactNode
  defaultOpen?: boolean
  actions?: React.ReactNode
  children: React.ReactNode
}

function Section({ title, icon, badge, defaultOpen = true, actions, children }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="rounded-lg border border-border">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</span>
        {badge}
        <div className="flex-1" />
        {/* Actions (click stops propagation) */}
        {actions && open && (
          <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
            {actions}
          </div>
        )}
        {open ? (
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div className="border-t border-border px-3 pb-3 pt-2">
          {children}
        </div>
      )}
    </div>
  )
}

// ─── Train Mode (Left Panel) ───────────────────────────────────

export function TrainMode() {
  const { state } = useCreateModel()
  const { startCaptioning, cancelCaptioning } = useAutoCaption()
  const hasImages = state.images.length > 0
  const isCaptioning = state.captioningStatus === 'running'
  const uncaptionedCount = state.images.filter((img) => img.captionStatus === 'none').length
  const captionedCount = state.images.length - uncaptionedCount

  return (
    <div className="flex flex-col gap-3">
      {/* ── Section 1: Images ── */}
      <Section
        title="Images"
        icon={<Images className="h-3.5 w-3.5" />}
        defaultOpen={true}
        badge={
          hasImages ? (
            <span className={cn(
              'rounded-full bg-muted px-2 py-0.5 text-[10px]',
              state.images.length < MIN_IMAGES ? 'text-amber-500' : 'text-muted-foreground',
            )}>
              {state.images.length}{state.images.length < MIN_IMAGES ? ` / ${MIN_IMAGES} min` : ''}
            </span>
          ) : null
        }
        actions={
          hasImages ? (
            <>
              {/* Auto-caption */}
              {isCaptioning ? (
                <div className="flex items-center gap-1.5">
                  <Progress
                    value={(state.captioningProgress / state.images.length) * 100}
                    size="sm"
                    className="w-16"
                  />
                  <Button variant="ghost" size="sm" onClick={cancelCaptioning} className="h-6 gap-1 px-2 text-[10px]">
                    <Loader2 className="h-2.5 w-2.5 animate-spin" />
                    Stop
                  </Button>
                </div>
              ) : uncaptionedCount > 0 ? (
                <Button variant="ghost" size="sm" onClick={startCaptioning} className="h-6 gap-1 px-2 text-[10px]">
                  <Sparkles className="h-2.5 w-2.5" />
                  Caption ({uncaptionedCount})
                </Button>
              ) : (
                <span className="text-[10px] text-green-500">All captioned</span>
              )}
            </>
          ) : undefined
        }
      >
        {/* Upload zone or grid */}
        {!hasImages ? (
          <UploadZone />
        ) : (
          <div className="flex flex-col gap-2">
            <ImageGrid />
          </div>
        )}

        {/* Validation warnings */}
        {hasImages && <ValidationWarnings />}
      </Section>

      {/* ── Section 2: Processing ── */}
      {hasImages && (
        <Section
          title="Processing"
          icon={<Wand2 className="h-3.5 w-3.5" />}
          defaultOpen={false}
        >
          <PreprocessToolbar />
        </Section>
      )}
    </div>
  )
}
